import 'primeicons/primeicons.css'
import '../css/app.css'
import './bootstrap'

import { useInertiaLocaleSync } from '@/composables/useInertiaLocaleSync'
import { useSentryUserSync } from '@/composables/useSentryUserSync'
import GuestLayout from '@/layouts/GuestLayout.vue'
import LegalLayout from '@/layouts/LegalLayout.vue'
import PdfExportLayout from '@/layouts/PdfExportLayout.vue'
import { i18n } from '@/plugins/i18n'
import matomoPlugin from '@/plugins/matomo'
import metaPixelPlugin from '@/plugins/meta-pixel'
import primeVuePlugin from '@/plugins/primevue'
import sentryPlugin from '@/plugins/sentry'
import { reportError } from '@/utils/error-reporting'
import { createInertiaApp, Link, router } from '@inertiajs/vue3'
import * as Sentry from '@sentry/vue'
import { createHead } from '@unhead/vue/client'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createApp, DefineComponent, h } from 'vue'

import FinestLayout from '@/layouts/FinestLayout.vue'
import '@twicpics/components/style.css'
import TwicPics from '@twicpics/components/vue3'

const appName = import.meta.env.VITE_APP_NAME || 'FinEstHub'

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const head = createHead()

createInertiaApp({
  title: (title) => (title ? `${title} • ${appName}` : appName),
  resolve: (name) => {
    const pages = import.meta.glob<DefineComponent>('./pages/**/*.vue', {
      eager: true,
    })
    // TODO: split into separate chunks for investors and banks

    const page = pages[`./pages/${name}.vue`]

    if (page.default.layout) {
      return page
    }

    if (name.startsWith('investors/') || name.startsWith('banks/')) {
      page.default.layout = FinestLayout
    } else if (name.startsWith('export/')) {
      page.default.layout = PdfExportLayout
    } else if (name.startsWith('legal/')) {
      page.default.layout = LegalLayout
    } else {
      page.default.layout = GuestLayout
    }

    return page
  },
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })

    primeVuePlugin(app)
    const sentryEnabled = sentryPlugin(app, props.initialPage.props.app.locale)

    // eslint-disable-next-line vue/no-reserved-component-names -- <Link> is Inertia's documented component name; renaming it would break every template.
    app.component('Link', Link)

    app.use(TwicPics, {
      domain: import.meta.env.VITE_TWICPICS_DOMAIN || 'https://finesthub.twic.pics',
    })

    app.use(head)
    app.use(plugin)
    app.use(pinia)
    app.use(i18n)

    useInertiaLocaleSync(props.initialPage.props)

    if (sentryEnabled) {
      useSentryUserSync(props.initialPage.props)
    }

    matomoPlugin()
    metaPixelPlugin(props.initialPage.component, props.initialPage.props.app.meta.pixelIds)
    app.mount(el)

    if (!sentryEnabled) {
      return
    }

    const isFeedbackWidgetHidden = (component: string, impersonating: boolean): boolean =>
      component.startsWith('export/') || impersonating

    let widget = isFeedbackWidgetHidden(
      props.initialPage.component,
      Boolean(props.initialPage.props.auth.impersonating),
    )
      ? undefined
      : Sentry.feedbackIntegration().createWidget()

    // Show or hide Sentry feedback widget based on route
    router.on('navigate', (event) => {
      if (isFeedbackWidgetHidden(event.detail.page.component, Boolean(event.detail.page.props.auth.impersonating))) {
        widget?.removeFromDom()
      } else {
        widget ??= Sentry.feedbackIntegration().createWidget()
      }
    })
  },
  progress: {
    color: '#1567E9',
  },
}).catch((error: unknown) => reportError('Starting the Inertia app failed', error))
